(ns frontend.view.modals.risk-editor.view
  (:require
   [basic-components.attachments :as attachments-view]
   [clojure.data :as data]
   [clojure.set :as set]
   [clojure.string :as string]
   [common.entity :as entity]
   [common.features.plan :as plan]
   [common.intelligence.common :as ic]
   [common.localization :as loc]
   [common.roles :as roles]
   [common.schema.constraints :as constraints]
   [common.schema.field :as field]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.actions.entity :as entity-actions]
   [frontend.components.field-editor :as field-editor]
   [frontend.logging :as logging]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.tenant-customizations :as tenant-customizations]
   [frontend.utils :as fu]
   [frontend.view.components.upload :as upload]
   [frontend.view.entity-viewers.activity.insider-activity :as insider-activity]
   [frontend.view.entity-viewers.events.editor-utils :as eu]
   [frontend.view.entity-viewers.utils :as entity-utils]
   [frontend.view.modals.confirmation.view :as confirmation-modal]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.risk-editor.events :as events]
   [frontend.view.syncing-data :as syncing]
   [frontend.zap-time :as zap-time]
   [reagent.core :as r]
   [ui.color :as c]
   [ui.components.alert.view :as alert]
   [ui.components.button.view :as button]
   [ui.components.modal.view :as modal]
   [ui.components.radix-ui.popover.view :as popover]
   [ui.components.select.select :as select]
   [ui.components.tab-strip.view :as tab-strip]
   [ui.dom-globals :as dg]))

(defonce modal-key :risk-editor-modal)

(defn entity-editor
  [state error-state
   {:keys [full-entity-schema entity-atom mandatory-fields props]}]
  (let [entity-template (some->> full-entity-schema
                                 (remove (fn [field-schema]
                                           (let [visibility (->> field-schema
                                                                 :field/default-visibility
                                                                 :editor)
                                                 required-role (->> field-schema
                                                                    :field/default-authorization
                                                                    :access/write)

                                                 ;; maybe one day email can have it's own permission config ???
                                                 hide? (when (or (= visibility :editor.type/none)
                                                                 (not (isa? roles/role-hierarchy
                                                                            @session/role
                                                                            required-role)))
                                                         :not-shown)]
                                             hide?))))
        entity-template-lookup (->> entity-template
                                    (map (fn [field-schema]
                                           [(:attribute field-schema) field-schema]))
                                    (into {}))
        {:keys [prevent-backend-update?]} props
        field-count (count entity-template)]
    [:div
     {:class "py-4"}
     (doall
      (->>
       entity-template
       (sort-by :field/default-ord)
       (map-indexed
        (fn [idx
             {:keys [:attribute :field/aggregate-attribute :field/particular-value]
              :as   field-schema}]
          (let [entity @entity-atom
                resolve-value (fn []
                                (if (and aggregate-attribute (fn? particular-value))
                                  (set (particular-value (get @entity-atom aggregate-attribute)
                                                         @session/entities))
                                  (get @entity-atom attribute)))
                value (resolve-value)]
            (when (and (let [in-use-filter? (:field/in-use-for-this-data? field-schema)]
                         (or (not (fn? in-use-filter?))
                             (in-use-filter? entity
                                             @session/entity-types
                                             session/has-feature-access?
                                             session/has-entity-access?)))
                       (not (:field/secondary-field? field-schema))                                 ;; secondary fields are shown in related fields in editor
                       (not= (:editor/type field-schema) :editor.type/none)
                       (or (not= (:attribute field-schema) :common/related-entities-commitments)    ;; commitments require fundraising feature + module access
                           (and (plan/available :feature/fundraising @session/tenant @session/role)
                                (session/has-module-access? @session/account :module/fundraising))))
              ^{:key (str "entity-creator-editor-" attribute)}
              [field-editor/view
               (merge
                field-schema
                {:id                    (str "entity-creator-editor-" attribute)
                 :entity-types-atom     session/entity-types
                 :drag-options          {:hide-drag-area? true}
                 :state-atom            state                                                       ;; state is passed  from outside so debugging is easier
                 :value                 value
                 :containing-entity     entity
                 :read-only?            (or (session/is-read-only-user?)
                                            (not (f-permissions/field-write-access? attribute (:entity/type entity) nil))
                                            (not (some #{(:access/type field-schema)}
                                                       [:field.access/write :field.access/create])))
                 ;; TODO: take language from user / tenant config -> default to :en
                 ;; if only :en in field/translations use :en
                 :label                 (or (->> field-schema
                                                 :field/translations
                                                 :en)
                                            (loc/tr attribute)
                                            (when-let [fs (registry/get-field-schema
                                                           @session/tenant-schema
                                                           attribute)]
                                              (loc/field-name attribute fs)))                       ;; we select label outside of field-editor so we can customize label if needed
                 :mandatory?            (and (not (session/is-read-only-user?))
                                             (boolean (some #{attribute} mandatory-fields)))        ;; can be function or boolean - most likely always view dependent
                 :placeholder           (field/placeholder field-schema)
                 :close-menu-on-scroll? true
                 :menu-portal-target    (dg/element-by-id "react-select-menu-container")
                 ;; tools are configured by need - all handlers are inside as currently the tools have no side effects
                 :tools                 [:tool-item/cancel :tool-item/clear]
                 :error-text            nil                                                         ;; TODO: use editor-atom to store validation and save errors
                 :validate!             (fn [attribute value]
                                          (let [field-constraints (:field/constraints field-schema)
                                                errors (constraints/validate field-constraints
                                                                             (:db/id entity)
                                                                             value
                                                                             entity
                                                                             (partial
                                                                              session/unique-check
                                                                              false
                                                                              (partial
                                                                               routing/open-entity))
                                                                             loc/tr)
                                                ;;skip person email validation if value is empty
                                                errors (if (and (= :person/universal-email-address
                                                                   attribute)
                                                                (= "" value))
                                                         nil
                                                         errors)]
                                            (when-not (:field/aggregate-attribute field-schema)
                                              (swap! state assoc attribute value))
                                            (deref state)
                                            (if errors
                                              (swap! error-state assoc attribute errors)
                                              (swap! error-state dissoc attribute))

                                            errors))
                 :on-save!
                 (fn [_attribute editor-new-value on-success! _on-error!]
                   ;;optimistic save to frontend db:
                   (let [save-attribute (or aggregate-attribute attribute)
                         old-attribute-value (get @entity-atom save-attribute)

                         edited-value (if-not aggregate-attribute
                                        editor-new-value
                                        (let [new-value (set editor-new-value)
                                              old-value (resolve-value)
                                              added-values (set/difference new-value old-value)
                                              removed-values (set/difference old-value new-value)
                                              old-aggregate-value old-attribute-value]
                                          (-> old-aggregate-value
                                              (set/union added-values)
                                              (set/difference removed-values))))]
                     (if (or (= edited-value old-attribute-value)
                             (and (fu/empty-value? edited-value)
                                  (fu/empty-value? old-attribute-value)))
                       ;; no changes ->> OK
                       (do
                         (on-success!))
                       (do
                         ;; optimistic save
                         (swap! state assoc save-attribute edited-value)
                         (deref state)
                         (when-not prevent-backend-update?
                           (entity-actions/*->field-update!
                            (:db/id entity)
                            save-attribute
                            edited-value
                            (fn [_resp]
                              ;; save OK
                              (on-success!))
                            (fn [err]
                              (let [current-attribute-value (get @entity-atom save-attribute)]
                                ;; optimistic save rollback
                                (when (or (= edited-value current-attribute-value)
                                          (and (fu/empty-value? edited-value)
                                               (fu/empty-value? current-attribute-value)))
                                  (swap! state assoc save-attribute old-attribute-value)
                                  (deref state))
                                (logging/error "Entity-editor-modal : Failed to save attribute : "
                                               err)))))))))}
                (when (>= idx (- field-count 1))
                  {:menu-placement "top"})
                (when-let [secondary-attribute (:field/secondary-attribute field-schema)]           ;; secondary attribute
                  {:secondary-view-args
                   (let [sec-templ (get entity-template-lookup secondary-attribute)]
                     (merge
                      sec-templ
                      {:state-atom         state
                       :value              (or (get @entity-atom secondary-attribute)
                                               (when (= (:field/data-type sec-templ)
                                                        :data.type/currency-symbol)
                                                 (:tenant/default-currency @session/tenant)))
                       :menu-portal-target (dg/element-by-id "react-select-menu-container")
                       :label              (loc/tr secondary-attribute)                                 ;; when we have custom dynamic fields the labels come from tenant configuration
                       :mandatory?         (boolean (some #{attribute} mandatory-fields))
                       :error-text         nil
                       }))}))]))))))]))

(defn editor-body
  [state error-state {:keys [edit-template entity-type mandatory-fields props]}]
  (let [hide-alert! identity]
    [:div
     (when (:alert @state)
       [:div
        [alert/danger
         {:on-close hide-alert!}
         (:alert @state)]])
     [entity-editor state error-state
      {:full-entity-schema edit-template
       :entity-atom        state
       :entity-type        entity-type
       :props              props
       :mandatory-fields   mandatory-fields}]]))

(defn associations-editor-body
  [state error-state {:keys [edit-template entity-type props]}]
  (let [hide-alert! identity
        primary-entity-id (or (:risk/primary-entity @(:entity-state props))
                              (:entity-id props))
        parent-entity (session/get-entity primary-entity-id)]
    [:div
     [:div
      {:class "pt-4"}
      [:div
       {:class "p-3 bg-info-light text-info-light-text font-semibold rounded"}
       (loc/tr :risk-parent-entity-label
         [(cu/str-trim-lcase (loc/tr (:entity/type parent-entity)))
          (entity/display-string parent-entity)])]]
     (when (:alert @state)
       [:div
        [alert/danger
         {:on-close hide-alert!}
         (:alert @state)]])
     [entity-editor state error-state
      {:full-entity-schema edit-template
       :entity-atom        state
       :entity-type        entity-type
       :props              props}]]))

(def MAX-ATTACHMENT-SIZE (* 50 1024 1024))

(defn attachments-editor-body
  [state]
  (let [hide-alert! identity]
    [:div
     (when (:alert @state)
       [:div
        [alert/danger
         {:on-close hide-alert!}
         (:alert @state)]])
     (let [attachments (->> @state
                            :common/attachments
                            (keep session/get-entity)
                            (remove :common/disabled?)
                            (sort-by :common/created-on)
                            reverse)
           common-attachments (set attachments)
           current-attachments-size (->> common-attachments
                                         (map :attachment/content-length)
                                         (filter number?)
                                         (apply +))
           pending-attachments-size (->> @state
                                         :uploading
                                         (map :size)
                                         (filter number?)
                                         (apply +))
           total-attachments-size (+ current-attachments-size
                                     pending-attachments-size)
           on-success! (fn [body]
                         (let [old-attachments (:common/attachments @state)
                               new-attachments (if (seq old-attachments)
                                                 (conj old-attachments (:db/id body))
                                                 #{(:db/id body)})]
                           (swap! state assoc :common/attachments new-attachments)))]
       [:div.attachment-selectors__upload-zone.pt-3
        #_[:div.mb-3
           [alert/alert
            {:variant :info}
            [:span
             [text/text (loc/tr :attachments-risk-info)]]]]
        [upload/custom-upload-zone
         {:class-name "value-creation-upload-zone"
          :on-change! (fn [{:keys [files original-event] :as args}]
                        (let [size (->> (aget files 0)
                                        (.-size))
                              new-size (+ (or size 0)
                                          total-attachments-size)]
                          (if (> new-size MAX-ATTACHMENT-SIZE)
                            (swap! state assoc :error (loc/tr :max-combined-attachment-size-error))
                            (events/add-attachment-to-entity! {:db/id       (:db/id @state)
                                                               :state       state
                                                               :on-success! on-success!
                                                               :on-error!   identity}
                                                              args))
                          (set! (-> original-event
                                    .-target
                                    .-value)
                                "")))
          :accept     ".jpg, .jpeg, .png, .pdf, .xlsx, .xls"}]
        [attachments-view/attachments-error state]
        [:div.attachment-listing.mb-3
         (doall
          (for [indicator (:uploading @state)]
            ^{:key (str "uploading-attachment-" (:token indicator))}
            [attachments-view/indicator-view state indicator]))
         (doall
          (for [attachment attachments]
            ^{:key (str "attachment-" (:db/id attachment))}
            [attachments-view/attachment-view-with-remove attachment (:db/id @state)
             (fn [removed-attachment]
               (swap! state assoc
                      :common/attachments
                      (disj (:common/attachments @state) (:db/id removed-attachment)))
               )]))]])]))

(defn submit-button
  [read-only? entity-state original-state state
   ok-button-icon ok-button-text on-ok! close!]
  (let [errors (events/validate-form @entity-state state)]
    (when-not read-only?
      (let [no-changes? (let [[removed added _] (data/diff original-state @entity-state)]
                          (and (empty? removed)
                               (empty? added)))
            disabled? (or (seq errors)
                          no-changes?)
            tooltip (cond no-changes?
                          (loc/tr :no-changes)
                          (seq errors)
                          (->> errors
                               (string/join "\r\n")))]
        [button/primary-solid
         {:disabled? disabled?
          :tooltip   tooltip
          :on-click  (fn [_]
                       (if on-ok!
                         (on-ok! (:db/id @entity-state) close!)
                         (close!)))}
         (when ok-button-icon
           ok-button-icon)
         [:span
          (or ok-button-text
              (loc/tr :submit))]]))))

(defn editor-footer
  [original-state entity-state state {:keys [props close!]}]
  (let [{:keys [on-ok! on-remove! ok-button-icon ok-button-text cancel-button-text cancel-icon]} props
        read-only? (session/is-read-only-user?)]
    [:<>
     [modal/left-single-button
      ::c/secondary
      false
      close!
      [:<>
       (when cancel-icon
         cancel-icon)
       [:span
        (if read-only?
          (loc/tr :close)
          (or cancel-button-text
              (loc/tr :cancel)))]]]
     [:div
      {:class "flex items-center justify-end gap-3"}
      (when (and (:db/id @entity-state)
                 (not read-only?))
        [button/danger-solid
         {:on-click (fn [_]
                      (if on-remove!
                        (modals/open-modal!
                         confirmation-modal/modal-key
                         {:body           (loc/tr :confirm-delete-risk)
                          :confirm-text   (loc/tr :delete)
                          :confirm-icon   ""
                          :skip-checkbox? true
                          :on-confirm     (fn [_]
                                            (on-remove! (:db/id @entity-state) close!)
                                            (close!))})
                        (close!)))}
         (loc/tr :remove)])

      [submit-button read-only? entity-state original-state state ok-button-icon ok-button-text
       on-ok! close!]]]))

(def min-spinner-time-in-ms 1000)

(defn tabstrip
  [state]
  [tab-strip/view
   {:on-change!       (fn [new-tab-key]
                        (swap! state assoc :selected-tab new-tab-key))
    :selected-tab     (:selected-tab @state)
    :class "*:pt-0"
    :tab-strip-id     "risk-modal-tabs"
    :tabs             [{:value ::properties
                        :label (loc/tr :risk-modal-properties)}
                       {:value ::associations
                        :label (loc/tr :risk-modal-associations)}
                       {:value ::attachments
                        :label (loc/tr :risk-modal-attachments)}]}])

(def default-field-order
  [:risk/title
   :risk/severity
   :risk/description
   :risk/mitigation
   :risk-holding/category
   :risk-holding/tags
   :risk/status
   :risk-holding/outcome])

(def capman-custom-field-label-lc->ord
  {"category"       0
   "title"          1
   "impact"         2
   ; CapMan has this typo in their custom field, they might fix it at some point
   "propability"    3
   "probability"    3

   "description"    4
   "mitigation"     5
   "actions taken"  6
   "current status" 7
   "status"         8})

(defn view
  [{:keys [_shown? _close-modal! props state _topmost?]}]
  (let [{:keys [mandatory-fields entity-state]} props
        original-state @entity-state
        entity-type (:entity/type original-state)
        error-state (r/cursor state [:errors])
        alert-state (r/cursor state [:server-errors])
        capman? (tenant-customizations/capman?)]
    (events/reset-state! state
                         error-state
                         alert-state
                         entity-type
                         mandatory-fields
                         original-state)
    (fn [{:keys [shown? close-modal! props state topmost?]}]
      (let [{:keys [on-cancel! title-text mandatory-fields read-only-fields]} props
            entity @(:entity-state props)
            entity-type (:entity/type entity)
            close! (fn []
                     (when on-cancel!
                       (on-cancel!))
                     (close-modal!))
            schema (registry/get-schema @session/tenant-schema entity-type)

            full-schema (->> schema
                             (filter
                              (fn [[attribute _]]
                                (f-permissions/field-read-access? attribute
                                                                  entity-type
                                                                  (fu/pipeline-id entity))))
                             (map (fn [[attribute field-schema]]
                                    (merge field-schema
                                           {:attribute   attribute
                                            :access/type (if (some #{attribute} read-only-fields)
                                                           :field.access/read
                                                           :field.access/write)
                                            :editor/type (entity-utils/resolve-editor-type attribute
                                                                                           field-schema)}))))
            property-template (->> full-schema
                                   (remove (fn [fs]
                                             (:field/aggregate-attribute fs)))
                                   (remove (fn [field-schema]
                                             (let [visibility (->> field-schema
                                                                   :field/default-visibility
                                                                   :editor)]
                                               (eu/no-related-entities? (:field/attribute field-schema))
                                               (when (= visibility :editor.type/none)
                                                 :not-shown))))
                                   (map-indexed
                                    (fn [idx fs]
                                      (let [field (:attribute fs)
                                            label (loc/field-name field fs)
                                            label-lc (string/lower-case (or label ""))
                                            custom-field-ord
                                            (cond
                                              capman? (get capman-custom-field-label-lc->ord label-lc)
                                              :else (cu/index-of default-field-order field))
                                            field-ord (or custom-field-ord (+ (count full-schema) idx))]
                                        (assoc fs :field/default-ord field-ord))))
                                   (sort-by :common/ord))
            finished? (and (:db/id @state)
                           (not (:processing @state)))
            processing? (or (:processing @state)
                            (when (< (- (zap-time/now) (:start-time @state)) min-spinner-time-in-ms)
                              (let [since-start (-> min-spinner-time-in-ms
                                                    (- (zap-time/now))
                                                    (+ (:start-time @state)))
                                    timeout (max 100 since-start)]
                                (js/setTimeout #(swap! state assoc :start-time 0) timeout)
                                true)))
            enriching? (or (:company/website (:draft-entity @state))
                           (:person/universal-email-address (:draft-entity @state)))]
        [modal/view
         {:visible?      (and shown? topmost?)
          :hide!         close-modal!
          :backdrop      "static"
          :close-button? (not finished?)}
         (or title-text (entity/display-string entity))
         [:<>
          (cond
            (and processing? enriching?)
            [syncing/sync-content]

            processing?
            [:div
             {:class "flex flex-col gap-3"}
             [:i.fal.fa-spinner.fa-pulse.fa-4x]
             [:span (loc/tr :processing)]]

            :else
            [:<>
             [tabstrip state]
             (let [selected-tab (get @state :selected-tab ::properties)]
               [:div
                {:class "min-h-[70vh]"}
                [:div
                 {:style {:display (if (= selected-tab ::properties) "block" "none")}}
                 [editor-body entity-state error-state
                  {:edit-template    property-template
                   :entity-type      entity-type
                   :props            props
                   :mandatory-fields mandatory-fields}]
                 (when (session/insider-groups?)
                   (let [insider-groups (->> @session/all-insider-groups
                                             (remove :common/disabled?))
                         active-insider-groups (->> insider-groups
                                                    (cu/filter-by :insider-group/status :insider-group.status/active)
                                                    (seq))]
                     (when active-insider-groups
                       (let [selected-group-id (:common/insider-group @entity-state)
                             selected-group (session/get-entity selected-group-id)
                             options (->> active-insider-groups
                                          (map (fn [k]
                                                 {:value k
                                                  :label (:insider-group/name k)}))
                                          (sort-by :label))]
                         [:div
                          {:class "px-3 flex flex-col gap-1"}
                          [:label
                           {:class "mb-0 text-sm"}
                           (loc/tr :common/insider-group)]

                          [select/view
                           {:value     (->> options
                                            (cu/filter-by :value selected-group-id)
                                            (first))
                            :on-change (fn [e]
                                         (swap! entity-state assoc :common/insider-group e)
                                         (deref entity-state))
                            :options   options}]
                          (when selected-group

                            [popover/view
                             {:open?          (:popover-open? @state)
                              :on-open-change (fn [open?]
                                                (swap! state assoc :popover-open? open?))}
                             [button/secondary-open
                              {:class    "underline"
                               :on-click (fn [e]
                                           (swap! state assoc :popover-open? (not (:popover-open? @state)))
                                           (fu/stop-n-prevent e))}
                              (loc/tr :show-insiders)]
                             [insider-activity/popup-view selected-group-id]])]))))
                 ]
                [:div
                 {:style {:display (if (= selected-tab ::associations) "block" "none")}}
                 [associations-editor-body entity-state error-state
                  {:edit-template    (->> full-schema
                                          (filter (fn [field-schema]
                                                    (:field/aggregate-attribute field-schema))))
                   :entity-type      entity-type
                   :props            props
                   :mandatory-fields mandatory-fields}]]
                [:div
                 {:style {:display (if (= selected-tab ::attachments) "block" "none")}}
                 [attachments-editor-body entity-state]]])])]
         (when-not (:processing @state)
           [editor-footer original-state entity-state state {:props  props
                                                             :close! close!}])]))))
